import { useRef, type ReactNode } from 'react';
import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import clsx from 'clsx';
import { LoaderCircleIcon, UploadIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { SearchInput } from '@/shared/ui/SearchInput';
import { UPLOADABLE } from '../model/useMediaUpload';

/**
 * Searching and uploading, over the same set of pictures.
 *
 * Both act on the library rather than on this frame: a search narrows what is offered, and an upload
 * adds to what could be. The two sit together because they are the two ways of answering "the one I
 * want is not here".
 */
export const MediaSearchRow = ({
  search,
  onSearchChange,
  onUpload,
  onOpenUpload,
  isUploading = false,
  uploadDisabledReason,
  toolbar = false,
  placeholder,
  accept = UPLOADABLE,
  className = '',
  children,
}: {
  search: string;
  onSearchChange: (value: string) => void;
  /** Uploads a picked file immediately. Ignored once `onOpenUpload` is given. */
  onUpload?: (files: readonly File[]) => void;
  /**
   * Opens a review step instead of uploading straight away. When present, the button no longer
   * carries its own hidden picker -- whatever it opens is responsible for choosing and confirming
   * files itself.
   */
  onOpenUpload?: () => void;
  isUploading?: boolean;
  /** When set, the upload is disabled and this is the reason shown in its tooltip. */
  uploadDisabledReason?: string;
  /**
   * Lays the row out for a full page rather than a side panel: the search stops stretching to
   * whatever width it is given, and the upload becomes the page's primary action, labelled.
   * In a narrow panel the same treatment would leave the search too short to read a filename in.
   */
  toolbar?: boolean;
  /**
   * What the search box asks for. Defaults to the media wording, which is right where the row sits
   * over pictures alone; a library that also holds typefaces says what it really searches.
   */
  placeholder?: string;
  /**
   * What the file picker offers, as a native `accept` string. Defaults to everything the library
   * takes; a surface that can only use some of it narrows this rather than accepting a file it
   * will refuse afterwards.
   */
  accept?: string;
  className?: string;
  /**
   * Further controls for the same library, placed between the search and the upload. Whichever of
   * them carries `ml-auto` decides where the row splits, so a caller can put a cluster next to the
   * upload without the row having to know what it is.
   */
  children?: ReactNode;
}) => {
  const { t } = useTranslation('tools');
  const searchLabel = placeholder ?? t('contentStudioV2.inspector.searchMedia');
  const picker = useRef<HTMLInputElement>(null);
  // The toolbar prints its label beside a column of media, where the noun is already on screen; the
  // icon-only form has only its accessible name to say what kind of thing it takes.
  const uploadLabel = toolbar
    ? t('contentStudioV2.inspector.upload')
    : t('contentStudioV2.inspector.uploadMedia');

  const uploadButton = (
    <Button
      variant={toolbar ? 'default' : 'outline'}
      size={toolbar ? 'default' : 'icon'}
      // `ml-auto` only when nothing was slotted in: with children the caller has already chosen
      // where the row splits, and a second auto margin would halve the gap between the two.
      className={
        toolbar ? clsx(children === undefined && 'ml-auto', 'shrink-0') : 'size-8 shrink-0'
      }
      disabled={isUploading || uploadDisabledReason !== undefined}
      onClick={() => (onOpenUpload !== undefined ? onOpenUpload() : picker.current?.click())}
      aria-label={toolbar ? undefined : uploadLabel}
    >
      {isUploading ? (
        <LoaderCircleIcon className="size-4 animate-spin" />
      ) : (
        <UploadIcon className="size-4" />
      )}
      {toolbar && uploadLabel}
    </Button>
  );

  return (
    <div className={`flex items-center gap-1.5 ${className}`}>
      <SearchInput
        // Everything in the toolbar sits at the default 36px so the row lines up with the tab-based
        // toggles a caller drops into it; the panel stays on the tighter 32px it was built for.
        size={toolbar ? 'default' : 'sm'}
        className={toolbar ? 'w-80' : 'flex-1'}
        value={search}
        onChange={onSearchChange}
        placeholder={searchLabel}
        aria-label={searchLabel}
      />

      {children}

      {onOpenUpload === undefined && (
        // The input is what opens the picker; the button is what a person sees. Kept hidden rather
        // than styled, because a file input cannot be restyled reliably across browsers.
        <input
          ref={picker}
          data-testid="media-upload-input"
          type="file"
          accept={accept}
          multiple
          className="hidden"
          onChange={(event) => {
            const files = Array.from(event.target.files ?? []);
            if (files.length > 0) {
              onUpload?.(files);
            }
            // Cleared so choosing the same file twice still fires a change. Without this, an upload
            // that failed cannot be retried by picking the same picture again.
            event.target.value = '';
          }}
        />
      )}

      {/* Only the icon-only form gets a tooltip: in the toolbar the same words are already printed
          on the button, and a floating copy of a visible label is noise. Unless the button is
          disabled, where the tooltip is the only place the reason is written. */}
      {uploadDisabledReason !== undefined ? (
        <TooltipProvider>
          <Tooltip>
            {/* The span takes the pointer events a disabled button drops, so the reason still shows. */}
            <TooltipTrigger asChild>
              <span className="inline-flex">{uploadButton}</span>
            </TooltipTrigger>
            <TooltipContent>{uploadDisabledReason}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : toolbar ? (
        uploadButton
      ) : (
        // Its own provider rather than the app's: this row is shared code, mounted by more than
        // one host, and a tooltip whose provider is missing throws rather than degrading.
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>{uploadButton}</TooltipTrigger>
            <TooltipContent>{uploadLabel}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
};
